import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { supabase } from './supabaseClient';

const AuthContext = createContext(null);

// Generate a random partner code
function generatePartnerCode() {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let code = '';
  for (let i = 0; i < 6; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return code;
}

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [partner, setPartner] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchProfile = useCallback(async (userId) => {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();
    
    if (error && error.code === 'PGRST116') {
      // Profile doesn't exist, create it
      const newCode = generatePartnerCode();
      const { data: newProfile, error: createError } = await supabase
        .from('profiles')
        .insert([{
          id: userId,
          partner_code: newCode,
          created_at: new Date().toISOString()
        }])
        .select()
        .single();
      
      if (!createError) {
        setProfile(newProfile);
      }
    } else if (!error && data) {
      // Check if partner_code is missing and generate one
      if (!data.partner_code) {
        console.log('No partner code found, generating one...');
        const newCode = generatePartnerCode();
        const { error: updateError } = await supabase
          .from('profiles')
          .update({ partner_code: newCode })
          .eq('id', userId);
        
        if (!updateError) {
          data.partner_code = newCode;
        }
      }
      
      setProfile(data);
      
      // If user has a partner, fetch partner info
      if (data.partner_id) {
        const { data: partnerData } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', data.partner_id)
          .single();
        setPartner(partnerData);
      }
    }
  }, []);

  useEffect(() => {
    // Check active session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchProfile(session.user.id);
      }
      setLoading(false);
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('Auth event:', event);
      
      // Handle password recovery
      if (event === 'PASSWORD_RECOVERY') {
        setUser(session?.user ?? null);
        setLoading(false);
        return;
      }
      
      setUser(session?.user ?? null);
      if (session?.user) {
        // Small delay to ensure user is fully created
        setTimeout(() => fetchProfile(session.user.id), 500);
      } else {
        setProfile(null);
        setPartner(null);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, [fetchProfile]);

  const signIn = useCallback(async (email, password, role = "laiba") => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) return { error };

    // Update profile with selected role
    if (data.user) {
      await supabase
        .from('profiles')
        .update({ role })
        .eq('id', data.user.id);
    }

    return { error: null };
  }, []);

  const signUp = useCallback(async (email, password, name, role) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          name,
          role: role || 'laiba'
        },
        emailRedirectTo: window.location.origin
      }
    });
    
    if (error) throw error;
    
    // Create profile with role
    if (data.user) {
      const partnerCode = generatePartnerCode();
      const { error: profileError } = await supabase
        .from('profiles')
        .upsert({
          id: data.user.id,
          email: email,
          name: name,
          role: role || 'laiba',
          partner_code: partnerCode,
          created_at: new Date().toISOString()
        });
      
      if (profileError) console.error('Profile creation error:', profileError);
    }
    
    return { data, error };
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setProfile(null);
    setPartner(null);
  };

  const updateProfile = async (updates) => {
    if (!user) return;
    const { error } = await supabase
      .from('profiles')
      .update(updates)
      .eq('id', user.id);
    if (!error) {
      setProfile(prev => ({ ...prev, ...updates }));
    }
    return !error;
  };

  const updatePassword = async (newPassword) => {
    const { error } = await supabase.auth.updateUser({
      password: newPassword
    });
    if (!error) {
    }
    return { error };
  };

  const value = {
    user,
    profile,
    partner,
    loading,
    signIn,
    signUp,
    signOut,
    updateProfile,
    updatePassword,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
